import {
  ConfigureEnvironmentRequestV1,
  CreateCredentialRequestV1,
  CreateOperatorRequestV1,
  CreateOrganizationRequestV1,
  CreatedCredentialResponseV1,
  DeactivatedPlatformAdministratorResponseV1,
  DeactivatePlatformAdministratorRequestV1,
  InvitationIssuedResponseV1,
  ResetPlatformAdministratorTwoFactorRequestV1,
  InviteFirstAdministratorRequestV1,
  InvitePlatformAdministratorRequestV1,
  OperatorDeactivationRequestV1,
  OperatorLinkedResponseV1,
  OperatorOrganizationResponseV1,
  OrganizationCreatedResponseV1,
  PlatformAdministratorListResponseV1,
  PlatformCredentialListResponseV1,
  PlatformCredentialV1,
  PlatformEnvironmentResponseV1,
  PlatformInvitationListQueryV1,
  PlatformInvitationListResponseV1,
  PlatformOrganizationListQueryV1,
  PlatformOrganizationAccessV1,
  PlatformOrganizationListResponseV1,
  PlatformOperatorListResponseV1,
  PlatformOperatorV1,
  PutProductRequestV1,
  RevokeCredentialRequestV1,
  RevokePlatformInvitationRequestV1,
} from "@strust/contracts";
import { z } from "zod";
import { defineEndpoint } from "../../../shared/api/endpoint.ts";

// Every operation of platform-administration (task 20.6), declared with the contract schemas.
// Served by `operator-auth` (operators, their environment, products, credentials) and `iam`. No
// route takes an environment: an operator has one per installation, the installation's (design D6).

export const listOperatorsEndpoint = defineEndpoint({
  method: "GET",
  path: "/platform/operators",
  response: PlatformOperatorListResponseV1,
});
export const getOperatorEndpoint = defineEndpoint({
  method: "GET",
  path: "/platform/operators/:id",
  response: PlatformOperatorV1,
});
export const configureEnvironmentEndpoint = defineEndpoint({
  method: "PATCH",
  path: "/platform/operators/:id/environment",
  body: ConfigureEnvironmentRequestV1,
  response: PlatformEnvironmentResponseV1,
});
export const putProductEndpoint = defineEndpoint({
  method: "PUT",
  path: "/platform/operators/:id/environment/products/:product",
  body: PutProductRequestV1,
  response: PlatformEnvironmentResponseV1,
});
/** Terminal (spec platform-administration, "Operator deactivation"): answers the operator. */
export const deactivateOperatorEndpoint = defineEndpoint({
  method: "POST",
  path: "/platform/operators/:id/deactivate",
  body: OperatorDeactivationRequestV1,
  response: PlatformOperatorV1,
});
export const listCredentialsEndpoint = defineEndpoint({
  method: "GET",
  path: "/platform/operators/:id/credentials",
  response: PlatformCredentialListResponseV1,
});
export const createCredentialEndpoint = defineEndpoint({
  method: "POST",
  path: "/platform/operators/:id/credentials",
  body: CreateCredentialRequestV1,
  response: CreatedCredentialResponseV1,
});
export const revokeCredentialEndpoint = defineEndpoint({
  method: "POST",
  path: "/platform/credentials/:id/revoke",
  body: RevokeCredentialRequestV1,
  response: PlatformCredentialV1,
});

export const createOrganizationEndpoint = defineEndpoint({
  method: "POST",
  path: "/platform/organizations",
  body: CreateOrganizationRequestV1,
  response: OrganizationCreatedResponseV1,
});
/**
 * Creates an operator inside the organization (one action: an operator always belongs to one
 * organization, design D1 of onboard-operators-within-organizations); answers both ids.
 */
export const createOperatorInOrganizationEndpoint = defineEndpoint({
  method: "POST",
  path: "/platform/organizations/:id/operators",
  body: CreateOperatorRequestV1,
  response: OperatorLinkedResponseV1,
});
export const inviteFirstAdministratorEndpoint = defineEndpoint({
  method: "POST",
  path: "/platform/organizations/:id/invitations",
  body: InviteFirstAdministratorRequestV1,
  response: InvitationIssuedResponseV1,
});
export const invitePlatformAdministratorEndpoint = defineEndpoint({
  method: "POST",
  path: "/platform/administrators/invitations",
  body: InvitePlatformAdministratorRequestV1,
  response: InvitationIssuedResponseV1,
});

export const listOrganizationsEndpoint = defineEndpoint({
  method: "GET",
  path: "/platform/organizations",
  query: PlatformOrganizationListQueryV1,
  response: PlatformOrganizationListResponseV1,
});
/**
 * The access overview of one organization (design D16 of reorganize-platform-area-by-organization):
 * its first Administrator, the counts of active console users and Administrators, and the state of
 * its identity provider. Never a link, token, secret or another user's email.
 */
export const getOrganizationAccessEndpoint = defineEndpoint({
  method: "GET",
  path: "/platform/organizations/:id/access",
  response: PlatformOrganizationAccessV1,
});
export const getOperatorOrganizationEndpoint = defineEndpoint({
  method: "GET",
  path: "/platform/operators/:id/organization",
  response: OperatorOrganizationResponseV1,
});
export const listPendingInvitationsEndpoint = defineEndpoint({
  method: "GET",
  path: "/platform/invitations",
  query: PlatformInvitationListQueryV1,
  response: PlatformInvitationListResponseV1,
});
export const revokePlatformInvitationEndpoint = defineEndpoint({
  method: "POST",
  path: "/platform/invitations/:id/revoke",
  body: RevokePlatformInvitationRequestV1,
  // Answers `204`: the client hands `undefined` to the schema.
  response: z.undefined(),
});

// Platform Administrators section (add-console-account-settings, design D10).
export const listPlatformAdministratorsEndpoint = defineEndpoint({
  method: "GET",
  path: "/platform/administrators",
  response: PlatformAdministratorListResponseV1,
});
export const deactivatePlatformAdministratorEndpoint = defineEndpoint({
  method: "POST",
  path: "/platform/administrators/:id/deactivate",
  body: DeactivatePlatformAdministratorRequestV1,
  response: DeactivatedPlatformAdministratorResponseV1,
});
/** Served only while the release flag `iam.two-factor` is on (design D7). */
export const resetPlatformAdministratorTwoFactorEndpoint = defineEndpoint({
  method: "POST",
  path: "/platform/administrators/:id/two-factor/reset",
  body: ResetPlatformAdministratorTwoFactorRequestV1,
  response: InvitationIssuedResponseV1,
});
